import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { SUBSCRIPTION_LIMITS, type PlanType, getPlanLimits, canAddUser, canAddLicense, canAddApplication, canAddPeople, hasFeature } from "@/../../shared/subscription-limits";

interface SubscriptionData {
  hasActiveSubscription: boolean;
  plan?: {
    name: string;
    priceId: string;
    status: string;
    currentPeriodEnd: string | null;
    currentPeriodStart: string;
    lastPaymentDate: string;
    nextBillingDate: string | null;
    amount: number;
    interval?: 'month' | 'year' | 'lifetime';
    currency: string;
  };
  features?: string[];
  redirectToSubscription: boolean;
  message?: string;
  usage: {
    users: number;
    licenses: number;
    applications: number;
    people?: number;
  };
  limits: {
    users: number;
    licenses: number;
    applications: number;
    people?: number;
  };
  planType?: PlanType;
  status?: string;
  isActive?: boolean;
  needsPayment?: boolean;
  isNewUser?: boolean;
  stripeSubscriptionId?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEndsAt?: string;
  error?: string;
  currentUsage?: {
    users: number;
    licenses: number;
    applications: number;
    people: number;
  };
  isTrialUser?: boolean;
  trialDaysLeft?: number;
}

export function useSubscription() {
  const { data: subscription, isLoading, error } = useQuery({
    queryKey: ['/api/subscription-status'],
    queryFn: async () => {
      const response = await apiRequest('/api/subscription-status');
      return response as SubscriptionData;
    },
  });

  // Extract plan type from the subscription state. Trial flags win because some
  // tenants can retain an old paid planId while running an active free trial.
  let planType: PlanType = 'trial';
  if (subscription?.isTrialUser || subscription?.plan?.status === 'trialing' || subscription?.status === 'trialing' || subscription?.plan?.name === 'Trial') planType = 'trial';
  else if (subscription?.plan?.name === 'Single') planType = 'single';
  else if (subscription?.plan?.name === 'Team') planType = 'team';
  else if (subscription?.plan?.name === 'Enterprise') planType = 'enterprise';
  else if (subscription?.planType) planType = subscription.planType;
  
  const limits = getPlanLimits(planType);
  const usage = subscription?.usage || subscription?.currentUsage || { users: 0, licenses: 0, applications: 0, people: 0 };

  return {
    subscription,
    planType,
    limits,
    usage,
    isLoading,
    error,
    
    // Permission checks
    canAddUser: () => canAddUser(planType, usage.users),
    canAddLicense: () => canAddLicense(planType, usage.licenses),
    canAddApplication: () => canAddApplication(planType, usage.applications),
    canAddPeople: () => canAddPeople(planType, usage.people || 0),
    hasFeature: (feature: keyof typeof SUBSCRIPTION_LIMITS.trial.features) => hasFeature(planType, feature),
    
    // Usage status
    isAtUserLimit: () => usage.users >= limits.maxUsers,
    isAtLicenseLimit: () => usage.licenses >= limits.maxLicenses,
    isAtApplicationLimit: () => usage.applications >= limits.maxApplications,
    isAtPeopleLimit: () => (usage.people || 0) >= limits.maxPeople,
    
    // Usage percentages
    getUserUsagePercentage: () => Math.round((usage.users / limits.maxUsers) * 100),
    getLicenseUsagePercentage: () => Math.round((usage.licenses / limits.maxLicenses) * 100),
    getApplicationUsagePercentage: () => Math.round((usage.applications / limits.maxApplications) * 100),
    getPeopleUsagePercentage: () => Math.round(((usage.people || 0) / limits.maxPeople) * 100),

    // Subscription status
    isTrialPlan: () => planType === 'trial',
    isPaidPlan: () => planType !== 'trial',
    needsUpgrade: () => planType === 'trial',
    isTrialing: () => subscription?.status === 'trialing',
    isActive: () => subscription?.isActive === true,
  };
}
